import React, { useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { Upload, X } from "lucide-react";
import { useModalDismiss } from "./useModalDismiss";

interface Props {
    categoryId: string;
    categoryName: string;
    onSave: () => void;
    onCancel: () => void;
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function DocumentForm({ categoryId, categoryName, onSave, onCancel }: Props) {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    function handleFile(f: File | null) {
        setFile(f);
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!file) { toast.error("Please select a file"); return; }
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        form.append("categoryId", categoryId);
        axios.post("/api/manuals/documents", form, {
            headers: { "Content-Type": "multipart/form-data" },
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Document uploaded");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to upload document");
                    setUploading(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to upload document");
                setUploading(false);
            });
    }

    function onDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Add a document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Documents show up in page lists and are searchable like pages.
                    </p>
                    <div
                        className={`m-doc-dropzone${dragging ? " is-dragging" : ""}`}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                    >
                        <Upload className="m-doc-dropzone-icon" size={32} />
                        <div className="m-doc-dropzone-primary">
                            {file ? "Click or drop to replace" : "Click to browse or drop a file here"}
                        </div>
                        <div className="m-doc-dropzone-hint">
                            PDF, Word, Excel, PowerPoint, text, HTML, CSV, or image
                        </div>
                        <input
                            ref={inputRef}
                            type="file"
                            style={{ display: "none" }}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.html,.htm,.csv,.png,.jpg,.jpeg,.gif"
                            onChange={e => handleFile(e.target.files?.[0] || null)}
                        />
                    </div>
                    {file && (
                        <div className="m-doc-file-info">
                            <span className="m-doc-file-name">{file.name}</span>
                            <span className="m-doc-file-size">{formatSize(file.size)}</span>
                            <button
                                type="button"
                                className="m-doc-file-remove"
                                onClick={e => { e.stopPropagation(); handleFile(null); }}
                                title="Remove file"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    )}
                    <div className="mb-3" style={{ marginTop: 16 }}>
                        <label className="m-label">Category</label>
                        <input type="text" className="m-input" value={categoryName} disabled />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={uploading || !file}>
                        {uploading ? "Uploading..." : "Upload"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
